import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Box, Tabs, Tab, Typography } from '@material-ui/core';
import { makeStyles, withStyles } from '@material-ui/styles';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import Files from '../Files';
import TabPanel from './TabPanel';

const StyledTab = withStyles((theme) => ({
  root: {
    padding: 0,
    textAlign: 'left',
    textWrap: 'nowrap',
    minWidth: 200,
    paddingRight: theme.spacing(2),
    fontSize: theme.typography.pxToRem(12),
    transition: 'border-right 0.3s',
    borderRight: '1px solid transparent',

    '&:hover': {
      borderRight: '1px solid #8ddcff'
    }
  },
  wrapper: {
    alignItems: 'flex-start'
  }
}))((props) => <Tab disableRipple {...props} />);

const useStyles = makeStyles((theme) => ({
  root: {
    paddingBottom: theme.spacing(3),
    display: 'flex'
  },
  tabs: {
    borderTop: `1px solid ${theme.palette.divider}`,
    borderRight: `1px solid ${theme.palette.divider}`,
    height: '90%',
    width: 'fit-content',
    paddingLeft: theme.spacing(2)
  },
  title: {
    paddingBottom: theme.spacing(0.75),
    borderRight: `1px solid ${theme.palette.divider}`,
    paddingLeft: theme.spacing(2),
    fontWeight: 700
  },
  indicator: {
    height: 8,
    width: 8,
    marginLeft: theme.spacing(1),
    marginBottom: 5,
    display: 'inline-flex',
    verticalAlign: 'middle',
    backgroundColor: '#E50046',
    borderRadius: 10
  },
  tabLabel: {
    cursor: 'pointer'
  },
  note: {
    position: 'sticky',
    top: 0,
    paddingBottom: theme.spacing(0.25),
    backgroundColor: theme.palette.background.paper,
    zIndex: 4
  },
  fileWrapper: {
    width: '100%'
  }
}));

function DocumentCategories({ claim, updateClaim, claimContactInformation, isGlassProcessor, userEmail, userName, onSetUserEmail, onSetUserName, fromGlassSearchView, documentData }) {
  const classes = useStyles();
  const { t } = useTranslation();
  const defaultTab = 'Photos';
  const { enqueueSnackbar } = useSnackbar();
  const successMessage = false;
  const errorMessage = false;
  const [activeCategory, setActiveCategory] = useState({});

  const [currentTab, setCurrentTab] = useState(defaultTab);
  const [tabs, setTabs] = useState([]);



  if (successMessage) {
    enqueueSnackbar(`${successMessage}`, {
      variant: 'success'
    });
  }

  if (errorMessage) {
    enqueueSnackbar(`${errorMessage}`, {
      variant: 'error'
    });
  }

  const uploadComplete = async () => {
    updateClaim();
  };



  useEffect(() => {
    setTabs(['Photos']);
    setActiveCategory(documentData['Photos'] || {});
  }, [claim]);

  return (
    <section className={classes.root} mt={3}>
      <nav>
        <Typography
          variant="button"
          color="textSecondary"
          className={classes.title}
          component="div"
        >
          {t('documentCategories.title')}
        </Typography>
        <Tabs
          orientation="vertical"
          scrollButtons="auto"
          textColor="secondary"
          value={currentTab}
          variant="scrollable"
          className={classes.tabs}
        >
          {tabs.map((tab) => {
            const label = (
              <label className={classes.tabLabel} htmlFor={tab}>
                {tab}
              </label>
            );
            return (
              <StyledTab
                id={tab}
                key={tab}
                label={label}
                value={tab}
              />
            )
          })}
        </Tabs>
      </nav>
      {tabs.map(tab => (
        <TabPanel
          key={tab}
          value={currentTab}
          index={tab}
          className={classes.tabPanel}
        >
          <Box className={classes.fileWrapper} aria-labelledby='document-section-note'>
            <Files
              category={documentData}
              uploadComplete={uploadComplete}
              claim={claim}
              claimContactInformation={claimContactInformation}
              userEmail={userEmail}
              userName={userName}
              onSetUserEmail={onSetUserEmail}
              onSetUserName={onSetUserName}
              fromGlassSearchView={fromGlassSearchView}
              isGlassProcessor={isGlassProcessor}
            />
          </Box>
        </TabPanel>
      ))}
    </section>
  );
}

DocumentCategories.propTypes = {
  claim: PropTypes.shape({
    attachments: PropTypes.arrayOf({
      attachmentUri: PropTypes.string
    }).isRequired,
    detail: PropTypes.shape({
      claimId: PropTypes.string
    }),
    docCategories: PropTypes.arrayOf(PropTypes.object),
    events: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.oneOf([PropTypes.number, PropTypes.string]),
        createdAt: PropTypes.string
      })
    ).isRequired,
  }),
  updateClaim: PropTypes.func.isRequired,
  claimContactInformation: PropTypes.object,
  isGlassProcessor: PropTypes.bool,
  userEmail: PropTypes.string,
  userName: PropTypes.string,
  onSetUserEmail: PropTypes.func,
  onSetUserName: PropTypes.func,
  fromGlassSearchView: PropTypes.bool,
  documentData: PropTypes.shape({
    docCategories: PropTypes.arrayOf(PropTypes.object),
    attachments: PropTypes.arrayOf(PropTypes.object),
    Photos: PropTypes.array
  }).isRequired
};

export default DocumentCategories;
